import React, { useState, useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Timeline,
  Table,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Spin,
  Row,
  Col,
  Statistic,
  Progress,
  Divider,
} from "antd";
import {
  ArrowLeftOutlined,
  SendOutlined,
  TruckOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  getOutsourceJob,
  markAsSent,
  updateOutsourceStatus,
  recordDelivery,
  updateQC,
  completeOutsource,
} from "../../api/outsourceApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const OutsourceJobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isLogged = Boolean(localStorage.getItem("isLogged"));

  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);

  // Modal states
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [qcModalOpen, setQcModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const [sendForm] = Form.useForm();
  const [deliveryForm] = Form.useForm();
  const [qcForm] = Form.useForm();
  const [completeForm] = Form.useForm();
  const [statusForm] = Form.useForm();

  useEffect(() => {
    const accessToken = localStorage.getItem("access-token");
    checkTokenExp(accessToken);
    fetchJob();
  }, [id]);

  const fetchJob = async () => {
    setLoading(true);
    try {
      const data = await getOutsourceJob(id);
      setJob(data);
    } catch (error) {
      toast.error("Failed to load outsource job details");
    }
    setLoading(false);
  };

  // ── Status helpers ─────────────────────────────────────────

  const getStatusConfig = (status) => {
    const configs = {
      created: { color: "default", text: "Created" },
      sent: { color: "processing", text: "Sent" },
      in_progress: { color: "blue", text: "In Progress" },
      partial_received: { color: "cyan", text: "Partial Received" },
      received: { color: "orange", text: "Received" },
      qc_passed: { color: "lime", text: "QC Passed" },
      qc_failed: { color: "red", text: "QC Failed" },
      completed: { color: "green", text: "Completed" },
      cancelled: { color: "default", text: "Cancelled" },
    };
    return configs[status] || { color: "default", text: status };
  };

  const getTimelineColor = (status) => {
    const map = {
      created: "gray",
      sent: "blue",
      in_progress: "blue",
      partial_received: "orange",
      received: "orange",
      qc_passed: "green",
      qc_failed: "red",
      completed: "green",
      cancelled: "red",
    };
    return map[status] || "gray";
  };

  // ── Action handlers ────────────────────────────────────────

  const handleSend = async (values) => {
    setModalLoading(true);
    try {
      await markAsSent(id, {
        tracking_number: values.tracking_number,
        notes: values.notes,
      });
      toast.success("Marked as sent");
      setSendModalOpen(false);
      sendForm.resetFields();
      fetchJob();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed");
    }
    setModalLoading(false);
  };

  const handleDelivery = async (values) => {
    setModalLoading(true);
    try {
      await recordDelivery(id, {
        received_qty: values.received_qty,
        rejected_qty: values.rejected_qty || 0,
        qc_status: values.qc_status || "pending",
        qc_notes: values.qc_notes,
        challan_no: values.challan_no,
        challan_date: values.challan_date
          ? values.challan_date.format("YYYY-MM-DD")
          : undefined,
        notes: values.notes,
      });
      toast.success("Delivery recorded");
      setDeliveryModalOpen(false);
      deliveryForm.resetFields();
      fetchJob();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed");
    }
    setModalLoading(false);
  };

  const handleQC = async (values) => {
    setModalLoading(true);
    try {
      await updateQC(id, {
        qc_status: values.qc_status,
        qc_notes: values.qc_notes,
      });
      toast.success("QC updated");
      setQcModalOpen(false);
      qcForm.resetFields();
      fetchJob();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed");
    }
    setModalLoading(false);
  };

  const handleComplete = async (values) => {
    setModalLoading(true);
    try {
      await completeOutsource(id, {
        actual_rate: values.actual_rate,
        vendor_invoice_no: values.vendor_invoice_no,
        vendor_invoice_date: values.vendor_invoice_date
          ? values.vendor_invoice_date.format("YYYY-MM-DD")
          : undefined,
        notes: values.notes,
      });
      toast.success("Outsource job completed");
      setCompleteModalOpen(false);
      completeForm.resetFields();
      fetchJob();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed");
    }
    setModalLoading(false);
  };

  const handleStatusUpdate = async (values) => {
    setModalLoading(true);
    try {
      await updateOutsourceStatus(id, {
        status: values.status,
        notes: values.notes,
      });
      toast.success("Status updated");
      setStatusModalOpen(false);
      statusForm.resetFields();
      fetchJob();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed");
    }
    setModalLoading(false);
  };

  // ── Delivery table columns ────────────────────────────────

  const deliveryColumns = [
    {
      title: "#",
      key: "index",
      render: (_, __, i) => i + 1,
      width: 50,
    },
    {
      title: "Date",
      dataIndex: "challan_date",
      key: "date",
      render: (d) => (d ? dayjs(d).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Challan No.",
      dataIndex: "challan_no",
      key: "challan_no",
      render: (v) => v || "-",
    },
    {
      title: "Received",
      dataIndex: "received_qty",
      key: "received_qty",
      render: (v) => <span style={{ color: "#52c41a" }}>{v?.toLocaleString()}</span>,
    },
    {
      title: "Rejected",
      dataIndex: "rejected_qty",
      key: "rejected_qty",
      render: (v) =>
        v > 0 ? (
          <span style={{ color: "#ff4d4f" }}>{v?.toLocaleString()}</span>
        ) : (
          0
        ),
    },
    {
      title: "QC Status",
      dataIndex: "qc_status",
      key: "qc_status",
      render: (s) => {
        const map = {
          pending: "default",
          passed: "green",
          failed: "red",
          partial_pass: "orange",
        };
        return <Tag color={map[s] || "default"}>{s?.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
    },
  ];

  // ── Render ─────────────────────────────────────────────────

  if (!isLogged) {
    return <Navigate to="/auth/login" replace />;
  }

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!job) {
    return (
      <Card>
        <p>Outsource job not found.</p>
        <Button onClick={() => navigate("/outsource")}>Go Back</Button>
      </Card>
    );
  }

  const statusConfig = getStatusConfig(job.status);
  const sent = job.sent_quantity || 0;
  const totalReceived = job.total_received || 0;
  const totalRejected = job.total_rejected || 0;
  const receivePct = sent > 0 ? Math.round((totalReceived / sent) * 100) : 0;

  // Allowed next transitions
  const transitionMap = {
    created: ["sent", "cancelled"],
    sent: ["in_progress", "partial_received", "received"],
    in_progress: ["partial_received", "received"],
    partial_received: ["received", "partial_received"],
    received: ["qc_passed", "qc_failed"],
    qc_passed: ["completed"],
    qc_failed: ["sent"],
  };
  const nextStatuses = transitionMap[job.status] || [];

  return (
    <div>
      <PageTitle title="Back" />

      {/* Header */}
      <Card style={{ marginBottom: 16 }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/outsource")}
              >
                Back
              </Button>
              <h2 style={{ margin: 0 }}>
                {job.outsource_number}{" "}
                <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
              </h2>
            </Space>
          </Col>
          <Col>
            <Space>
              {job.status === "created" && (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={() => setSendModalOpen(true)}
                >
                  Send to Vendor
                </Button>
              )}
              {["sent", "in_progress", "partial_received"].includes(
                job.status
              ) && (
                <Button
                  icon={<TruckOutlined />}
                  onClick={() => setDeliveryModalOpen(true)}
                >
                  Record Delivery
                </Button>
              )}
              {job.status === "received" && (
                <Button
                  icon={<CheckCircleOutlined />}
                  onClick={() => setQcModalOpen(true)}
                >
                  Update QC
                </Button>
              )}
              {job.status === "qc_passed" && (
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                  onClick={() => setCompleteModalOpen(true)}
                >
                  Complete
                </Button>
              )}
              {nextStatuses.length > 0 && (
                <Button onClick={() => setStatusModalOpen(true)}>
                  Change Status
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={16}>
        {/* Left: Details */}
        <Col xs={24} lg={16}>
          {/* Job & Vendor Info */}
          <Card title="Job & Vendor Details" style={{ marginBottom: 16 }}>
            <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Job">
                <a onClick={() => navigate(`/job/${job.job?.id}`)}>
                  {job.job?.job_number}
                </a>
              </Descriptions.Item>
              <Descriptions.Item label="Customer">
                {job.job?.customer?.name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Vendor">
                {job.vendor?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Service">
                <Tag>
                  {job.service_type?.replace(/_/g, " ").toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Stage">
                {job.stage?.name || job.stage_id || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Expected Date">
                {job.expected_date
                  ? dayjs(job.expected_date).format("DD MMM YYYY")
                  : "-"}
                {job.expected_date &&
                  dayjs(job.expected_date).isBefore(dayjs()) &&
                  !["completed", "cancelled", "qc_passed"].includes(
                    job.status
                  ) && (
                    <Tag color="red" style={{ marginLeft: 6 }}>
                      OVERDUE
                    </Tag>
                  )}
              </Descriptions.Item>
              <Descriptions.Item label="Quoted Rate">
                {job.quoted_rate ? `₹${job.quoted_rate}` : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Tracking #">
                {job.tracking_number || "-"}
              </Descriptions.Item>
              {job.specifications && (
                <Descriptions.Item label="Specifications" span={2}>
                  <pre style={{ margin: 0, fontSize: 12 }}>
                    {typeof job.specifications === "string"
                      ? job.specifications
                      : JSON.stringify(job.specifications, null, 2)}
                  </pre>
                </Descriptions.Item>
              )}
              {job.notes && (
                <Descriptions.Item label="Notes" span={2}>
                  {job.notes}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Quantity Progress */}
          <Card title="Quantity Tracking" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic title="Sent" value={sent} />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Received"
                  value={totalReceived}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Rejected"
                  value={totalRejected}
                  valueStyle={{
                    color: totalRejected > 0 ? "#ff4d4f" : undefined,
                  }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Pending"
                  value={sent - totalReceived}
                  valueStyle={{
                    color:
                      sent - totalReceived > 0 ? "#fa8c16" : "#52c41a",
                  }}
                />
              </Col>
            </Row>
            <Progress
              percent={receivePct}
              status={receivePct >= 100 ? "success" : "active"}
              style={{ marginTop: 12 }}
            />
          </Card>

          {/* Deliveries */}
          <Card
            title="Delivery History"
            style={{ marginBottom: 16 }}
            extra={
              ["sent", "in_progress", "partial_received"].includes(
                job.status
              ) && (
                <Button
                  size="small"
                  icon={<TruckOutlined />}
                  onClick={() => setDeliveryModalOpen(true)}
                >
                  Record
                </Button>
              )
            }
          >
            <Table
              columns={deliveryColumns}
              dataSource={job.deliveries || []}
              rowKey={(r, i) => r.id || i}
              pagination={false}
              size="small"
              locale={{ emptyText: "No deliveries recorded yet" }}
            />
          </Card>

          {/* Completion details */}
          {job.status === "completed" && (
            <Card title="Completion Details" style={{ marginBottom: 16 }}>
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Actual Rate">
                  {job.actual_rate ? `₹${job.actual_rate}` : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Vendor Invoice">
                  {job.vendor_invoice_no || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Invoice Date">
                  {job.vendor_invoice_date
                    ? dayjs(job.vendor_invoice_date).format("DD MMM YYYY")
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Total Value">
                  {job.actual_rate
                    ? `₹${(job.actual_rate * totalReceived).toLocaleString()}`
                    : "-"}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}
        </Col>

        {/* Right: Timeline */}
        <Col xs={24} lg={8}>
          <Card title="Status Timeline">
            <Timeline
              items={(job.status_history || []).map((entry) => ({
                color: getTimelineColor(entry.status),
                children: (
                  <div>
                    <div>
                      <Tag color={getStatusConfig(entry.status).color}>
                        {getStatusConfig(entry.status).text}
                      </Tag>
                    </div>
                    <small style={{ color: "#888" }}>
                      {entry.created_at
                        ? dayjs(entry.created_at).format(
                            "DD MMM YYYY, hh:mm A"
                          )
                        : ""}
                    </small>
                    {entry.notes && (
                      <div style={{ fontSize: 12, marginTop: 2 }}>
                        {entry.notes}
                      </div>
                    )}
                    {entry.user?.name && (
                      <div style={{ fontSize: 11, color: "#aaa" }}>
                        by {entry.user.name}
                      </div>
                    )}
                  </div>
                ),
              }))}
            />
            {(!job.status_history || job.status_history.length === 0) && (
              <p style={{ color: "#999", textAlign: "center" }}>
                No status history available
              </p>
            )}
          </Card>
        </Col>
      </Row>

      {/* ── Modals ── */}

      {/* Send to Vendor */}
      <Modal
        title="Send to Vendor"
        open={sendModalOpen}
        onCancel={() => setSendModalOpen(false)}
        onOk={() => sendForm.submit()}
        confirmLoading={modalLoading}
        destroyOnClose
      >
        <Form form={sendForm} layout="vertical" onFinish={handleSend}>
          <Form.Item name="tracking_number" label="Tracking / Challan No.">
            <Input placeholder="TRK-12345" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Record Delivery */}
      <Modal
        title="Record Delivery"
        open={deliveryModalOpen}
        onCancel={() => setDeliveryModalOpen(false)}
        onOk={() => deliveryForm.submit()}
        confirmLoading={modalLoading}
        destroyOnClose
      >
        <Form
          form={deliveryForm}
          layout="vertical"
          onFinish={handleDelivery}
          initialValues={{
            challan_date: dayjs(),
            received_qty: sent - totalReceived,
            qc_status: "pending",
          }}
        >
          <Form.Item
            name="received_qty"
            label="Quantity Received"
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <Form.Item name="rejected_qty" label="Quantity Rejected">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <Form.Item name="qc_status" label="QC Status">
            <Select>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="passed">Passed</Select.Option>
              <Select.Option value="failed">Failed</Select.Option>
              <Select.Option value="partial_pass">Partial Pass</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="challan_no" label="Challan No.">
            <Input />
          </Form.Item>
          <Form.Item name="challan_date" label="Challan Date">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="qc_notes" label="QC Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* QC Update */}
      <Modal
        title="Update QC Status"
        open={qcModalOpen}
        onCancel={() => setQcModalOpen(false)}
        onOk={() => qcForm.submit()}
        confirmLoading={modalLoading}
        destroyOnClose
      >
        <Form form={qcForm} layout="vertical" onFinish={handleQC}>
          <Form.Item
            name="qc_status"
            label="QC Status"
            rules={[{ required: true }]}
          >
            <Select placeholder="Select QC result">
              <Select.Option value="passed">Passed</Select.Option>
              <Select.Option value="failed">Failed</Select.Option>
              <Select.Option value="partial_pass">Partial Pass</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="qc_notes" label="QC Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Complete */}
      <Modal
        title="Complete Outsource Job"
        open={completeModalOpen}
        onCancel={() => setCompleteModalOpen(false)}
        onOk={() => completeForm.submit()}
        confirmLoading={modalLoading}
        destroyOnClose
      >
        <Form form={completeForm} layout="vertical" onFinish={handleComplete}>
          <Form.Item name="actual_rate" label="Actual Rate (₹ per piece)">
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              precision={2}
              prefix="₹"
            />
          </Form.Item>
          <Form.Item name="vendor_invoice_no" label="Vendor Invoice No.">
            <Input placeholder="VINV-2026-100" />
          </Form.Item>
          <Form.Item name="vendor_invoice_date" label="Vendor Invoice Date">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Change Status (generic) */}
      <Modal
        title="Change Status"
        open={statusModalOpen}
        onCancel={() => setStatusModalOpen(false)}
        onOk={() => statusForm.submit()}
        confirmLoading={modalLoading}
        destroyOnClose
      >
        <Form
          form={statusForm}
          layout="vertical"
          onFinish={handleStatusUpdate}
        >
          <Form.Item
            name="status"
            label="New Status"
            rules={[{ required: true }]}
          >
            <Select placeholder="Select status">
              {nextStatuses.map((s) => (
                <Select.Option key={s} value={s}>
                  {getStatusConfig(s).text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OutsourceJobDetail;
